// AutomationRuleConditions.jsx

import React from 'react';

// Utilities
import {
  applyConditionOperatorChange,
  buildDefaultConditionLiteral,
  isDateLikeFieldType,
} from '../../utils/automationConditionFilters';
import {
  flattenConditionGroups,
  getAllowedConditionOperators,
  getConditionOperatorLabel,
  getDefaultConditionOperator,
  normalizeJoinToNext,
} from '../../utils/automationRuleUtils';

// CSS
import './AutomationRuleConditions.css';

// Components
import ConditionHint from './components/ConditionHint';
import ConditionValueEditor from './components/ConditionValueEditor';
import Button from 'components/button/Button';
import SelectInput from 'components/selectinput/SelectInput';

/**
 * AutomationRuleConditions
 *
 * Renders editable condition rows for an automation rule.
 *
 * @param {object} rule - The parent rule containing condition rows.
 * @param {object} theme - Theme object used for dynamic styling.
 * @param {array} fieldOptions - Available fields for condition field selection.
 * @param {Map} fieldTypeByKey - Field type lookup keyed by field key.
 * @param {Map} fieldEnumOptionsByKey - Enum option lookup keyed by field key.
 * @param {function} updateConditionRows - Updates flattened condition rows for a rule.
 * @param {function} handleRemoveCondition - Removes a condition row from a rule.
 *
 * @returns {ReactNode} The rendered condition rows.
 */
const AutomationRuleConditions = React.memo(({
  rule,
  theme,
  fieldOptions,
  fieldTypeByKey,
  fieldEnumOptionsByKey,
  updateConditionRows,
  handleRemoveCondition,
}) => {

  const conditionGroups = Array.isArray(rule.conditionGroups) && rule.conditionGroups.length
    ? rule.conditionGroups
    : [{ key: `fallback-${rule.key}`, conditions: Array.isArray(rule.conditions) ? rule.conditions : [] }];

  // ----- RENDER ----- //

  return (
    <div className="automation-modal-rows-container">

      {flattenConditionGroups(conditionGroups).map((conditionRow, conditionIndex, rows) => {
        const conditionFieldType = fieldTypeByKey.get(conditionRow?.fieldKey || '') || 'string';
        const allowedConditionOperators = getAllowedConditionOperators(conditionFieldType);
        const selectedConditionOperator = allowedConditionOperators.includes(conditionRow?.operator)
          ? conditionRow.operator
          : getDefaultConditionOperator(conditionFieldType);
        const isDateCondition = isDateLikeFieldType(conditionFieldType);
        const literalValue = conditionRow?.value?.literal;
        const isNumericBetween = (
          conditionFieldType === 'number'
          || conditionFieldType === 'money'
          || conditionFieldType === 'percentage'
        ) && selectedConditionOperator === 'between';
        const showBetweenHint = isNumericBetween;
        const showOlderThanHint = isDateCondition && selectedConditionOperator === 'older_than';
        const isLastRow = conditionIndex === rows.length - 1;

        return (
          <div className="automation-modal-action-block" key={conditionRow.key}>
            <div className="automation-rule-row automation-rule-row-four">
              <div>
                <SelectInput
                  style={{
                    fontSize: '12px',
                  }}
                  value={conditionRow?.fieldKey || ''}
                  options={fieldOptions.map((field) => ({
                    value: field.key,
                    label: field.label,
                  }))}
                  placeholder="Select field..."
                  onChange={(event) => {
                    const nextFieldKey = event.target.value;
                    updateConditionRows(rule.key, (flatRows) => flatRows.map((entry) => {
                      if (entry.key !== conditionRow.key) return entry;
                      const nextFieldType = fieldTypeByKey.get(nextFieldKey) || 'string';
                      const nextOperator = getDefaultConditionOperator(nextFieldType);
                      return {
                        ...entry,
                        fieldKey: nextFieldKey,
                        operator: nextOperator,
                        value: {
                          type: 'literal',
                          literal: buildDefaultConditionLiteral(nextFieldType, nextOperator),
                        },
                      };
                    }));
                  }}
                />
              </div>

              <div>
                <SelectInput
                  style={{
                    fontSize: '12px',
                  }}
                  value={selectedConditionOperator}
                  options={allowedConditionOperators.map((operator) => ({
                    value: operator,
                    label: getConditionOperatorLabel(operator),
                  }))}
                  onChange={(event) => {
                    updateConditionRows(rule.key, (flatRows) => flatRows.map((entry) => {
                      if (entry.key !== conditionRow.key) return entry;
                      return applyConditionOperatorChange({
                        fieldType: conditionFieldType,
                        operator: event.target.value,
                        currentCondition: entry,
                      });
                    }));
                  }}
                />
              </div>

              <div>
                <ConditionValueEditor
                  rule={rule}
                  conditionRow={conditionRow}
                  conditionFieldType={conditionFieldType}
                  selectedConditionOperator={selectedConditionOperator}
                  literalValue={literalValue}
                  theme={theme}
                  fieldTypeByKey={fieldTypeByKey}
                  fieldEnumOptionsByKey={fieldEnumOptionsByKey}
                  updateConditionRows={updateConditionRows}
                />
              </div>

              {/* CONDITION TAIL */}
              <div className="automation-modal-condition-tail">
                {!isLastRow && (
                  <SelectInput
                    style={{
                      fontSize: '12px',
                    }}
                    value={normalizeJoinToNext(conditionRow?.joinToNext)}
                    title="Join to next condition"
                    options={[
                      { value: 'and', label: 'and' },
                      { value: 'or', label: 'or' },
                    ]}
                    onChange={(event) => {
                      const nextJoin = normalizeJoinToNext(event.target.value);
                      updateConditionRows(rule.key, (flatRows) => flatRows.map((entry) => {
                        if (entry.key !== conditionRow.key) return entry;
                        return {
                          ...entry,
                          joinToNext: nextJoin,
                        };
                      }));
                    }}
                  />
                )}

                <Button
                  iconKey="do_not_disturb_on"
                  iconSize="16"
                  foregroundColor={theme.foregroundMuted}
                  backgroundColor="transparent"
                  borderColor="transparent"
                  hoverBackgroundColor="transparent"
                  borderRadius="6px"
                  height="28px"
                  width="28px"
                  padding="0"
                  enabled={rows.length > 1}
                  title="Remove condition"
                  onClick={() => handleRemoveCondition(rule.key, conditionRow.key)}
                />
              </div>
            </div>

            <ConditionHint
              showBetweenHint={showBetweenHint}
              showOlderThanHint={showOlderThanHint}
              theme={theme}
            />
          </div>
        );
      })}
    </div>
  );
});

// Dev Tools
AutomationRuleConditions.displayName = 'AutomationRuleConditions';

export default AutomationRuleConditions;
